import { AxiosError, AxiosRequestConfig } from 'axios';
import { apiDelete, apiGet, apiPost, apiPut, apiPutFile } from './api';
import {
  DTOmarkConfigurationAsExternal,
  DTOunmarkExternalConfiguration,
  DTOupdateConfigurationStatus,
  IconfigurationNotCompleteReason,
  IOrganizationConfig,
  IOrganizationConfigCreate,
  IProductConfigAdditionalActionConfirmDto,
  IProductConfiguration,
  IProductConfigurationCreate,
  IProductConfigurationUpdateReseller
} from '../types/product.configuration.types';
const { REACT_APP_SUBSCRIPTION } = process.env;

/**
 * Obtiene las configuraciones de una organización por su ID
 */
export const getOrganizationConfigByOrganizationId = async (organizationId: number): Promise<IOrganizationConfig[]> => {
  return apiGet<AxiosRequestConfig, IOrganizationConfig[]>(
    `${REACT_APP_SUBSCRIPTION}/organization-config/by-organization/${organizationId}`
  )
    .then(resp => Promise.resolve(resp))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Obtiene las configuraciones de una organización en formato tabla con filtros
 */
export const getOrganizationConfigTableByOrganizationId = async (
  organizationId: number,
  filtersQuery: string,
  searchText: string
): Promise<IOrganizationConfig[]> => {
  return apiGet<AxiosRequestConfig, IOrganizationConfig[]>(
    `${REACT_APP_SUBSCRIPTION}/organization-config/table-by-organization/${organizationId}?${filtersQuery}&search=${searchText}`
  )
    .then(resp => Promise.resolve(resp))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Crea una nueva configuración para una organización
 */
export const addOrganizationConfig = async (
  createOrganizationConfigData: IOrganizationConfigCreate
): Promise<IOrganizationConfig> => {
  const formData = new FormData();
  const dataJson = JSON.stringify({
    organizationId: createOrganizationConfigData.organizationId,
    configClassId: createOrganizationConfigData.configClassId,
    name: createOrganizationConfigData.name || '',
    configData: createOrganizationConfigData.configData
  });
  formData.append('data', dataJson);
  if (createOrganizationConfigData.file) {
    formData.append('file', createOrganizationConfigData.file);
  }
  return apiPost<AxiosRequestConfig, FormData>(`${REACT_APP_SUBSCRIPTION}/organization-config`, formData)
    .then(resp => Promise.resolve(resp.data))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Actualiza el estado activo/inactivo de una configuración de organización
 */
export const updateOrganizationConfigActiveStatus = async (isActive: boolean, uuid: string): Promise<boolean> => {
  return apiPut<AxiosRequestConfig, { isActive: boolean }>(
    `${REACT_APP_SUBSCRIPTION}/organization-config/update-active-status/${uuid}`,
    {
      isActive
    }
  )
    .then(resp => Promise.resolve(resp.data))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Elimina una configuración de organización
 */
export const deleteConfiguration = async (uuid: string): Promise<boolean> => {
  return apiDelete<AxiosRequestConfig, { uuid: string }>(`${REACT_APP_SUBSCRIPTION}/organization-config/delete/${uuid}`)
    .then(resp => Promise.resolve(resp.data))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Obtiene las configuraciones de un producto por su ID
 */
export const getProductConfigurationByProductId = async (productId: number): Promise<IProductConfiguration[]> => {
  return apiGet<AxiosRequestConfig, IProductConfiguration[]>(
    `${REACT_APP_SUBSCRIPTION}/product-config/by-product/${productId}`
  )
    .then(resp => Promise.resolve(resp))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Crea una nueva configuración de producto
 */
export const addProductConfiguration = async (config: IProductConfigurationCreate): Promise<IProductConfiguration> => {
  return apiPost<AxiosRequestConfig, IProductConfigurationCreate>(`${REACT_APP_SUBSCRIPTION}/product-config`, config)
    .then(resp => Promise.resolve(resp.data))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Actualiza el reseller de una configuración de producto
 */
export const updateProductConfigurationReseller = async (
  config: IProductConfigurationUpdateReseller
): Promise<IProductConfiguration> => {
  return apiPut<AxiosRequestConfig, IProductConfigurationUpdateReseller>(
    `${REACT_APP_SUBSCRIPTION}/product-config/reseller`,
    config
  )
    .then(resp => Promise.resolve(resp.data))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Actualiza el estado de una configuración adicional de producto
 */
export const updateAdditionalConfigurationStatus = async (body: FormData) => {
  return apiPutFile(`${REACT_APP_SUBSCRIPTION}/product-config/set-status`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Confirma una acción adicional de una configuración de producto
 */
export const confirmConfigAdditionalAction = async (
  config: IProductConfigAdditionalActionConfirmDto
): Promise<string> => {
  return apiPut<AxiosRequestConfig, IProductConfigAdditionalActionConfirmDto>(
    `${REACT_APP_SUBSCRIPTION}/product-config-additional-actions/backoffice/confirm`,
    config
  )
    .then(resp => Promise.resolve(resp))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Marca una configuración como externa
 */
export const markConfigurationAsExternal = async (body: DTOmarkConfigurationAsExternal, file?: File) => {
  const formData = new FormData();
  const bodyJson = JSON.stringify(body);
  formData.append('data', bodyJson);
  file && formData.append('file', file);
  return apiPut<AxiosRequestConfig, FormData>(
    `${REACT_APP_SUBSCRIPTION}/logistic-service/backoffice/additional-external-service/set`,
    formData
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Desmarca una configuración como externa
 */
export const unmarkExternalConfiguration = async (body: DTOunmarkExternalConfiguration) => {
  return apiPut<AxiosRequestConfig, DTOunmarkExternalConfiguration>(
    `${REACT_APP_SUBSCRIPTION}/logistic-service/backoffice/additional-external-service/unset`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Actualiza el estado de una configuración
 */
export const updateConfigurationStatus = async (data: DTOupdateConfigurationStatus, files?: File[]) => {
  const formData = new FormData();
  files?.forEach(file => formData.append('files', file));
  formData.append('newData', JSON.stringify(data));
  return apiPutFile(`${REACT_APP_SUBSCRIPTION}/product-config/set-status`, formData)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Obtiene las opciones de razones por las cuales una configuración no fue completada
 */
export const getConfigurationNotCompleteReasonOptions = async () => {
  return apiGet<AxiosRequestConfig, IconfigurationNotCompleteReason[]>(
    `${REACT_APP_SUBSCRIPTION}/configuration-not-completed-reason`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Marca como vista la alerta de configuración no completada
 */
export const markConfigurationNotCompletedAlertViewed = async (uuid: string) => {
  return apiPut<AxiosRequestConfig, { uuid: string }>(
    `${REACT_APP_SUBSCRIPTION}/product-config-additional-actions/backoffice/confirm`,
    { uuid }
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};
